/**
 * Module 7: take, close and reopen (3 minutes). The first click takes the case, More
 * holds Take and Set status, Closed is not a reply, the auto-close rules, Back for you,
 * duplicates.
 *
 * Cases match practice/facts.ts: PRACTICE-0003 Demo Metals Ltd (closed 23 days ago,
 * customer wrote again 2 days ago, in Back for you); PRACTICE-0004 a colleague forward
 * of PRACTICE-0001. Round 3 (12 Oct 2026): Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "status",
  title: "Take, close and reopen",
  menuTitle: "Status",
  minutes: 3,
  kind: "lesson",
  goals: [
    "Take every case you work on.",
    "Know that Closed does not mean that the customer got a reply.",
    "Know when I2Q closes a case, how to reopen it, and what Back for you shows.",
    "Handle the same inquiry when it arrives twice.",
  ],
  helpTopics: ["statuses", "back-for-you"],
  slides: [
    {
      id: "s1",
      tone: "light",
      title: "When you start on a case, it is yours",
      job: "When you need to start on a case, do the first step. That takes the case, and colleagues see it.",
      path: ["Case", "More", "Set status"],
      body: [
        {
          kind: "text",
          text: "The big button never says Take. Your first click on a case takes it for you: a confirm, a pick or a prepare. To take a case without a change, open `More` and click `Take`, or press {key:t}. When a colleague has the case, the big button says `Take over`. The caption names the colleague. `Release` under `More` gives the case back.",
        },
        {
          kind: "text",
          text: "`Set status` under `More` holds the next steps: `Mark done`, `Not for us`, `Waiting for customer`, and `Reopen` on a closed case. After you copy the reply, the big button says `Mark done and next`.",
        },
        {
          kind: "keys",
          keys: [
            { key: "t", does: "Take the case" },
            { key: "d", does: "Mark done and next" },
            { key: "j", does: "Next case" },
            { key: "k", does: "Previous case" },
          ],
        },
      ],
      interaction: {
        id: "s1-take",
        kind: "practice",
        required: true,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "action" },
        task: "take-and-set-waiting",
        steps: [
          "Open `More` and click `Take`.",
          "Open `More` again. Under `Set status`, click `Waiting for customer`.",
        ],
        success:
          "The case is yours. It waits for the customer. Colleagues see that you have it. It comes back to you when it is due.",
        hints: [
          "`More` sits next to the big button.",
          "`Set status` is a group inside the `More` menu.",
        ],
      },
      check: "If you work on it, it is yours. Otherwise two people may quote the same inquiry.",
      labels: [
        "More",
        "Take",
        "Take over",
        "Release",
        "Set status",
        "Mark done",
        "Not for us",
        "Waiting for customer",
        "Reopen",
        "Mark done and next",
      ],
    },
    {
      id: "s2",
      tone: "grey",
      title: "Closed is not a reply",
      job: "Read why the case is closed before you trust it.",
      path: ["Sidebar", "Queue", "Closed"],
      body: [
        {
          kind: "text",
          text: "I2Q closes a case **in I2Q only**. It moves and sends nothing. It closes a case when:",
        },
        {
          kind: "list",
          items: [
            "somebody moved the mail to the done folder of the shared mailbox,",
            "somebody at Norican replied in the mail thread,",
            "the mail is your own reply,",
            "the mail follows an inquiry that is already closed,",
            "nobody touched the case for 21 days.",
          ],
        },
        {
          kind: "text",
          text: "A reopen, or another mail from the customer, brings the case back.",
        },
        {
          kind: "note",
          tone: "warning",
          title: "Moved to the done folder is not a reply",
          text: "In one area, somebody had moved almost every closed case to the done folder, with no reply. Many customers still waited.",
        },
      ],
      interaction: {
        id: "s2-sort",
        kind: "sort",
        required: true,
        prompt: "Did the customer get a reply?",
        bins: ["The customer got a reply", "Check the thread"],
        cards: [
          {
            id: "s2-done-folder",
            text: "Moved to the done folder of the shared mailbox",
            bin: 1,
            why: "Somebody moved the mail. That says nothing about a reply. Check the thread.",
          },
          {
            id: "s2-quotation",
            text: "Quotation sent on a date",
            bin: 0,
            why: "A quotation went to the customer. This customer got a reply.",
          },
          {
            id: "s2-by-sales",
            text: "Set to done by sales",
            bin: 1,
            why: "A colleague marked it done. That is usually right. Check the thread if the customer writes again.",
          },
          {
            id: "s2-no-activity",
            text: "No activity",
            bin: 1,
            why: "Nobody touched it for 21 days. The customer may still wait.",
          },
        ],
      },
      check:
        "Closed with Moved to the done folder of the shared mailbox? Open the thread and look for a reply.",
      labels: [
        "Closed",
        "Moved to the done folder of the shared mailbox",
        "Set to done by sales",
        "No activity",
      ],
    },
    {
      id: "s3",
      tone: "light",
      title: "Reopen a case that is still open",
      job: "Open Back for you and reopen the case the customer wrote about again.",
      path: ["Sidebar", "Back for you"],
      body: [
        {
          kind: "text",
          text: "The chip `Back for you` lists the cases that came back: a customer you confirmed wrote again, or a waiting case is due.",
        },
        {
          kind: "text",
          text: "A closed case says `Nothing to do` on its big button. The caption gives the reason. `Reopen` sits under `More`.",
        },
      ],
      interaction: {
        id: "s3-reopen",
        kind: "practice",
        required: true,
        stage: { view: "queue", preset: "back-for-you" },
        task: "reopen-closed",
        steps: [
          "Open the case from Demo Metals Ltd.",
          "Read the reason: closed 23 days ago, the customer wrote again 2 days ago.",
          "Open `More` and click `Reopen`.",
        ],
        success: "The case is open again. Demo Metals Ltd gets a reply.",
        hints: [
          "The case is in the `Back for you` list.",
          "`Reopen` is under `More`, in the `Set status` group of a closed case.",
        ],
      },
      check: "Open Back for you every morning. These customers already wait.",
      labels: ["Back for you", "Nothing to do", "Reopen", "More"],
    },
    {
      id: "s4",
      tone: "grey",
      title: "The same inquiry twice",
      job: "Check who has the first copy before you quote an inquiry that arrived twice.",
      body: [
        {
          kind: "text",
          text: "One inquiry can arrive twice: as a resend, as a forward from a colleague, or as a copy to two people. I2Q links the copies with one line under the subject, for example **Same inquiry as PRACTICE-0001**. The big button says `Nothing to do`.",
        },
      ],
      interaction: {
        id: "s4-scenario",
        kind: "scenario",
        required: true,
        start: "s4-start",
        stage: { view: "case", caseId: "PRACTICE-0004", part: "full" },
        nodes: {
          "s4-start": {
            text: "A colleague forwards the Example Foundry Ltd inquiry. It arrives as PRACTICE-0004, tagged From a colleague. What do you do first?",
            choices: [
              {
                id: "s4-quote",
                text: "Quote it at once.",
                outcome: "bad",
                fb: "Careful. PRACTICE-0001 is the same inquiry.",
                next: "s4-oops",
              },
              {
                id: "s4-who",
                text: "Open PRACTICE-0001 and look who has it.",
                outcome: "good",
                fb: "Right. Check first, then decide.",
                next: "s4-check",
              },
              {
                id: "s4-close",
                text: "Mark it Not for us.",
                outcome: "ok",
                fb: "Maybe. First make sure that somebody handles PRACTICE-0001.",
                next: "s4-check",
              },
            ],
          },
          "s4-oops": {
            text: "A colleague may quote PRACTICE-0001 at this moment. The customer could get two quotations with two prices. What do you do?",
            choices: [
              {
                id: "s4-oops-check",
                text: "Stop and check who has PRACTICE-0001.",
                outcome: "good",
                fb: "Right. Only one person replies to an inquiry.",
                next: "s4-check",
              },
              {
                id: "s4-oops-send",
                text: "Send it anyway.",
                outcome: "bad",
                fb: "No. The customer may get two replies with two prices.",
                next: "s4-check",
              },
            ],
          },
          "s4-check": {
            text: "PRACTICE-0001 is not assigned. Nobody works on it yet. What next?",
            choices: [
              {
                id: "s4-take",
                text: "Take PRACTICE-0001, reply to it, and mark PRACTICE-0004 done.",
                outcome: "good",
                fb: "Right. One person replies to the inquiry. If a colleague has it, leave it to the colleague.",
              },
              {
                id: "s4-both",
                text: "Reply to both cases.",
                outcome: "bad",
                fb: "No. The customer gets two replies, maybe with two prices.",
              },
              {
                id: "s4-leave",
                text: "Leave both. Somebody else takes it.",
                outcome: "bad",
                fb: "No. If everybody waits, nobody replies.",
              },
            ],
          },
        },
      },
      check: "Tagged From a colleague? Look for the first copy of the inquiry before you quote.",
      labels: ["From a colleague", "Same inquiry as", "Nothing to do"],
    },
    {
      id: "s5",
      tone: "light",
      title: "Check: an empty list",
      job: "Click the right option to finish the module.",
      interaction: {
        id: "s5-check",
        kind: "check",
        required: true,
        question: {
          id: "s5-q",
          topic: "status",
          stem: "Under `Filters` you set who has it to `Mine`. The list is empty, but your team has lots of work. Why?",
          options: [
            {
              id: "s5-q-a",
              text: "I2Q has not read the mail yet.",
              correct: false,
              fb: "No. The cases are there. They are not yours yet.",
            },
            {
              id: "s5-q-b",
              text: "Nobody took the cases.",
              correct: true,
              fb: "Right. `Mine` fills up only when cases are yours. Your first click on a case takes it.",
            },
            {
              id: "s5-q-c",
              text: "Somebody closed your cases.",
              correct: false,
              fb: "No. Closed cases sit in the Closed tab. `Mine` stays empty until you take a case.",
            },
            {
              id: "s5-q-d",
              text: "The filter is broken.",
              correct: false,
              fb: "No. The filter works. Nobody took a case, so nothing is yours.",
            },
          ],
        },
      },
      check: "Work on a case and it is yours. Then `Mine` shows your real work.",
      labels: ["Mine", "Filters"],
    },
    checkSlide({
      id: "s6",
      tone: "grey",
      title: "Check: it came back",
      topic: "status",
      stem: "A customer wrote again about a closed case. Where do you find it?",
      answers: [
        [
          "In `Back for you`.",
          true,
          "Right. Cases come back there when a customer you confirmed writes again.",
        ],
        [
          "Nowhere. Closed cases stay closed.",
          false,
          "No. Another mail from the customer brings the case back.",
        ],
        ["Only in Outlook.", false, "No. I2Q shows it in Back for you."],
        ["In `Purchase orders (parked)`.", false, "No. That tab holds purchase orders only."],
      ],
      check: "Open Back for you every morning.",
      labels: ["Back for you", "Purchase orders (parked)"],
    }),
    checkSlide({
      id: "s7",
      tone: "light",
      title: "Check: no activity",
      topic: "status",
      stem: "When does I2Q close a case that nobody touches?",
      answers: [
        ["After 1 day.", false, "No. Today is the default view of the queue, not a closing rule."],
        ["After 7 days.", false, "No. 7 days is the chip `open older than 7 days`."],
        ["Never.", false, "No. I2Q files it after 21 days."],
        [
          "After 21 days without activity, in I2Q only.",
          true,
          "Right. The customer may still wait. Reopen it if the customer still waits.",
        ],
      ],
      check: "The customer needs a reply, not silence. Check the thread.",
    }),
  ],
};
